import { ArrowLeftRight, Bell, LineChart, LogOut, Menu, Sun, Moon, TrendingUp, Users, Wallet } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLogout, useMe } from "@/hooks/useAuth";
import { useTraderDashboard } from "@/hooks/useTrader";
import { useAdminNotifications } from "@/hooks/useMisc";
import { useReadNotifications, markNotificationsRead } from "@/hooks/useNotificationReads";
import { useTheme } from "@/hooks/useTheme";

const PRIORITY_DOT: Record<string, string> = {
  critical: "bg-danger", high: "bg-warning", normal: "bg-primary", low: "bg-fg-subtle",
};

function NotificationBell() {
  const { data } = useAdminNotifications();
  const read = useReadNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const rows = data?.rows ?? [];
  const count = rows.filter((n) => !read.has(n.id)).length;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 z-50 rounded-lg border border-border bg-bg-elevated shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-sm font-semibold flex items-center justify-between">
            Notifications <span className="text-[11px] text-fg-subtle font-normal">{count} pending</span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border/30">
            {rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-fg-subtle">You're all caught up 🎉</p>
            ) : (
              rows.slice(0, 15).map((n) => (
                <button
                  key={n.id}
                  onClick={() => { markNotificationsRead([n.id]); setOpen(false); nav(n.link); }}
                  className="w-full text-left px-3 py-2 hover:bg-bg/60 transition flex items-start gap-2"
                >
                  <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[n.priority] ?? "bg-primary"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{n.title}</p>
                    <p className="text-[11px] text-fg-subtle truncate">{n.body}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); nav("/admin/notifications"); }}
            className="w-full px-3 py-2 text-center text-xs text-primary hover:bg-bg/60 border-t border-border/50"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segs = pathname.split("/").filter(Boolean);
  if (!segs.length) return null;
  return (
    <nav className="text-xs text-fg-subtle">
      <ol className="flex items-center gap-1.5">
        {segs.map((s, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <span className={i === segs.length - 1 ? "text-fg-muted capitalize" : "capitalize"}>
              {s.replace(/-/g, " ")}
            </span>
            {i < segs.length - 1 && <span className="opacity-40">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Topbar({ onMenuToggle }: { onMenuToggle?: () => void } = {}) {
  const { data: me } = useMe();
  const logout = useLogout();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { resolved, toggle } = useTheme();

  // A manager granted an add-on admin group can switch between their RM portal
  // and the back office within one login.
  const managerHasAdmin = me?.role === "manager" && (me.fullAccess || (me.permissions?.length ?? 0) > 0);
  const inAdmin = pathname.startsWith("/admin");

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-bg/70 px-4 sm:px-5 backdrop-blur">
      {onMenuToggle && (
        <button
          onClick={onMenuToggle}
          aria-label="Open menu"
          className="lg:hidden grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}
      <div className="flex flex-1 items-center gap-3 min-w-0">
        <Breadcrumbs />
      </div>

      <div className="flex items-center gap-2">
        {/* Portal switcher for managers granted an add-on admin role. */}
        {managerHasAdmin && (
          <Link
            to={inAdmin ? "/manager/dashboard" : "/admin/dashboard"}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-md bg-primary/10 hover:bg-primary/20 ring-1 ring-primary/30 px-2.5 py-1.5 text-xs font-semibold text-primary transition"
            title={inAdmin ? "Switch to your RM portal" : "Switch to the back office"}
          >
            <ArrowLeftRight className="h-4 w-4" /> {inAdmin ? "RM Area" : "Back Office"}
          </Link>
        )}
        {/* Trader-only quick actions — hidden for admin/manager/platform. */}
        {me?.role === "trader" && (
          <nav className="hidden sm:flex items-center gap-1.5 mr-1">
            <WalletPill />
            <Link to="/trader/web-trader" className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated/60 hover:bg-bg-elevated px-2.5 py-1.5 text-xs font-medium text-fg-muted hover:text-fg transition">
              <LineChart className="h-4 w-4" /> Web Trader
            </Link>
            <Link to="/trader/deposit" className="inline-flex items-center gap-1.5 rounded-md bg-grad-primary px-2.5 py-1.5 text-xs font-semibold text-white transition hover:opacity-90">
              <TrendingUp className="h-4 w-4" /> Deposit
            </Link>
          </nav>
        )}
        <button
          onClick={toggle}
          aria-label={resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={resolved === "dark" ? "Light mode" : "Dark mode"}
          className="grid h-9 w-9 place-items-center rounded-lg bg-bg-elevated/60 text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
        >
          {resolved === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>
        <NotificationBell />
        <button
          onClick={async () => {
            await logout.mutateAsync();
            nav("/login");
          }}
          className="ml-1 flex items-center gap-2 rounded-lg pl-1.5 pr-3 py-1 hover:bg-bg-elevated/60 transition"
          title={me?.email}
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-grad-primary text-white text-xs font-semibold">
            {(me?.fullName ?? "A").slice(0, 1).toUpperCase()}
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-success ring-2 ring-bg" />
          </span>
          <div className="hidden md:flex flex-col items-start leading-tight">
            <span className="text-xs font-medium text-fg">
              {me?.fullName ?? "Admin"}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
              {me?.role ?? "—"}
            </span>
          </div>
          <LogOut className="h-4 w-4 text-fg-subtle" />
        </button>
      </div>
    </header>
  );
}


/** Main-wallet balance pill for the trader top bar. */
function WalletPill() {
  const dash = useTraderDashboard();
  const balance = dash.data?.wallet ? Number(dash.data.wallet.balance) : null;
  return (
    <Link
      to="/trader/dashboard"
      title="Main wallet balance"
      className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated/60 hover:bg-bg-elevated px-2.5 py-1.5 text-xs font-semibold text-fg transition"
    >
      <Wallet className="h-4 w-4 text-primary" />
      {balance == null ? "…" : `$${balance.toFixed(2)}`}
    </Link>
  );
}
